import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getProgress, saveProgress } from '../services/api';

import WizardLayout from '../components/wizard/WizardLayout';
import { computeStepStatuses } from '../components/wizard/stepStatus';
import StepWelcome from '../components/wizard/StepWelcome';
import StepPersonal from '../components/wizard/StepPersonal';
import StepFamilyPersonal from '../components/wizard/StepFamilyPersonal';
import StepCareerIncome from '../components/wizard/StepCareerIncome';
import StepFinancialPlanning from '../components/wizard/StepFinancialPlanning';
import StepAccounts from '../components/wizard/StepAccounts';
import StepIncome from '../components/wizard/StepIncome';
import StepDebts from '../components/wizard/StepDebts';
import StepExpenses from '../components/wizard/StepExpenses';
import StepDocuments from '../components/wizard/StepDocuments';
import StepReview from '../components/wizard/StepReview';
import StepScheduleMeeting from '../components/wizard/StepScheduleMeeting';

const STEPS = [
  { key: 'schedule_meeting',   label: 'Schedule Meeting',     icon: 'bi-calendar2-check' },
  { key: 'welcome',            label: 'Welcome',              icon: 'bi-house-heart' },
  { key: 'personal',           label: 'Personal Info',        icon: 'bi-person-vcard' },
  { key: 'family_personal',    label: 'Family & Personal',    icon: 'bi-people' },
  { key: 'career_income',      label: 'Career & Income',      icon: 'bi-briefcase' },
  { key: 'financial_planning', label: 'Financial & Planning', icon: 'bi-graph-up' },
  { key: 'accounts',           label: 'Cash Accounts',        icon: 'bi-bank' },
  { key: 'income',             label: 'Recurring Income',     icon: 'bi-cash-coin' },
  { key: 'debts',              label: 'Debts & Loans',        icon: 'bi-credit-card' },
  { key: 'expenses',           label: 'Expenses',             icon: 'bi-receipt' },
  { key: 'documents',          label: 'Documents',            icon: 'bi-paperclip' },
  { key: 'review',             label: 'Review',               icon: 'bi-check2-all' },
];

function getMeetingStatus(scheduleData) {
  if (!scheduleData || typeof scheduleData !== 'object') return 'not_scheduled';

  const explicitStatus = String(scheduleData.meeting_status || '').trim().toLowerCase();
  if (explicitStatus === 'canceled' || explicitStatus === 'cancelled') {
    return 'canceled';
  }
  if (explicitStatus === 'scheduled') {
    return 'scheduled';
  }

  return 'not_scheduled';
}

function hasScheduledMeeting(scheduleData) {
  const normalizedStatus = getMeetingStatus(scheduleData);
  if (normalizedStatus === 'canceled') return false;
  if (normalizedStatus === 'scheduled') return true;
  if (!scheduleData || typeof scheduleData !== 'object') return false;
  if (scheduleData.booking_detected) return true;

  const bookingInfo = scheduleData.booking_data?.bookingInfo;
  if (bookingInfo) {
    const statusCode = String(bookingInfo.statusCode || '').trim();
    if (statusCode === '1') return true;

    const meetingTimes = bookingInfo.MeetingTimes;
    if (Array.isArray(meetingTimes) && meetingTimes.length > 0) return true;
  }

  const bookedStatus = String(scheduleData.booking_data?.bookedstatus || '').toLowerCase();
  if (bookedStatus === 'canceled' || bookedStatus === 'cancelled') return false;
  if (bookedStatus === 'scheduled') return true;

  return false;
}

/**
 * WizardPage — main FPR Update Form controller.
 *
 * Loads saved progress (including prefill defaults and the client
 * portal URL) on mount, renders the current step, and auto-saves as the
 * user navigates.
 */
export default function WizardPage() {
  const { client, logout } = useAuth();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [prefill, setPrefill] = useState(null);
  const [portalUrl, setPortalUrl] = useState('');
  const [scheduleMeetingUrl, setScheduleMeetingUrl] = useState('');
  const [secondaryAdvisor, setSecondaryAdvisor] = useState('');
  const [clientType, setClientType] = useState('hff');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadedRef = useRef(false);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    async function load() {
      try {
        const data = await getProgress();
        setCurrentStep(data.current_step || 0);
        setFormData(data.form_data || {});
        setPrefill(data.prefill || null);
        setPortalUrl(data.client_portal_url || '');
        setScheduleMeetingUrl(data.schedule_meeting_url || '');
        setSecondaryAdvisor(data.secondary_advisor || '');
        setClientType(data.client_type || 'hff');
      } catch (err) {
        console.error('Failed to load progress:', err);
        setError('Unable to load your saved progress. Please reload.');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const persist = useCallback(
    async (step, data, completed = false) => {
      setSaving(true);
      try {
        // Include the authenticated client id so the backend can reject
        // cross-tab identity mismatches instead of overwriting another form.
        await saveProgress(step, data, completed, client?.client_id || null);
        setError(null);
      } catch (err) {
        console.error('Save failed:', err);
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 409) {
          setError(
            detail
              || 'Session identity mismatch. Please reload using your original access link.',
          );
        } else {
          setError('Could not save — please check your connection.');
        }
      } finally {
        setSaving(false);
      }
    },
    [client?.client_id],
  );

  function goNext() {
    const currentKey = STEPS[currentStep]?.key;

    // Cannot leave the schedule step until a meeting is booked.
    if (
      currentKey === 'schedule_meeting'
      && !hasScheduledMeeting(formData?.schedule_meeting)
    ) {
      setError('Please schedule your meeting before continuing.');
      return;
    }

    const nextStep = currentStep + 1;
    const isLast = currentStep === STEPS.length - 1;

    if (isLast) {
      if (!hasScheduledMeeting(formData?.schedule_meeting)) {
        setError('Please schedule your meeting before finishing.');
        return;
      }
      persist(currentStep, formData, true).then(() =>
        navigate('/complete', { replace: true }),
      );
      return;
    }

    setCurrentStep(nextStep);
    persist(nextStep, formData);
    document.querySelector('.cashflow-step-content')?.scrollTo(0, 0);
  }

  function goSkip() {
    const stepKey = STEPS[currentStep]?.key;
    if (
      !stepKey
      || stepKey === 'welcome'
      || stepKey === 'review'
      || stepKey === 'schedule_meeting'
    ) return;
    const updated = {
      ...formData,
      [stepKey]: {
        ...(formData[stepKey] || {}),
        _skipped: true,
        none_of_the_above: true,
      },
    };
    setFormData(updated);
    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
    persist(nextStep, updated);
    document.querySelector('.cashflow-step-content')?.scrollTo(0, 0);
  }

  function goBack() {
    if (currentStep <= 0) return;
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    persist(prevStep, formData);
    document.querySelector('.cashflow-step-content')?.scrollTo(0, 0);
  }

  function jumpTo(idx) {
    if (idx === currentStep) return;
    // Block forward navigation past the schedule step until a meeting is booked.
    const scheduleIdx = STEPS.findIndex((s) => s.key === 'schedule_meeting');
    if (
      idx > scheduleIdx
      && !hasScheduledMeeting(formData?.schedule_meeting)
    ) {
      setError('Please schedule your meeting before continuing.');
      return;
    }
    setCurrentStep(idx);
    persist(idx, formData);
    document.querySelector('.cashflow-step-content')?.scrollTo(0, 0);
  }

  function sectionData(key) {
    return formData[key] || {};
  }

  function updateSection(key) {
    return (updated) => {
      setFormData((prev) => ({ ...prev, [key]: updated }));
    };
  }

  function updateScheduleSection(updated) {
    setFormData((prev) => {
      const next = { ...prev, schedule_meeting: updated };
      // Persist schedule state immediately so booking detection survives reloads.
      persist(currentStep, next);
      return next;
    });
  }

  function renderStep() {
    const stepKey = STEPS[currentStep]?.key;
    switch (stepKey) {
      case 'welcome':
        return <StepWelcome clientName={client?.name} />;
      case 'personal':
        return (
          <StepPersonal
            data={sectionData('personal')}
            onChange={updateSection('personal')}
            prefill={prefill}
          />
        );
      case 'family_personal':
        return (
          <StepFamilyPersonal
            data={sectionData('family_personal')}
            onChange={updateSection('family_personal')}
          />
        );
      case 'career_income':
        return (
          <StepCareerIncome
            data={sectionData('career_income')}
            onChange={updateSection('career_income')}
          />
        );
      case 'financial_planning':
        return (
          <StepFinancialPlanning
            data={sectionData('financial_planning')}
            onChange={updateSection('financial_planning')}
          />
        );
      case 'accounts':
        return (
          <StepAccounts
            data={sectionData('accounts')}
            onChange={updateSection('accounts')}
          />
        );
      case 'income':
        return (
          <StepIncome
            data={sectionData('income')}
            onChange={updateSection('income')}
            careerIncomeData={sectionData('career_income')}
          />
        );
      case 'debts':
        return (
          <StepDebts
            data={sectionData('debts')}
            onChange={updateSection('debts')}
          />
        );
      case 'expenses':
        return (
          <StepExpenses
            data={sectionData('expenses')}
            onChange={updateSection('expenses')}
          />
        );
      case 'documents':
        return (
          <StepDocuments
            data={sectionData('documents')}
            formData={formData}
            onChange={updateSection('documents')}
            portalUrl={portalUrl}
          />
        );
      case 'review':
        return <StepReview formData={formData} />;
      case 'schedule_meeting':
        return (
          <StepScheduleMeeting
            data={sectionData('schedule_meeting')}
            onChange={updateScheduleSection}
            schedulingUrl={scheduleMeetingUrl}
            formData={formData}
            prefill={prefill}
            clientName={client?.name || ''}
            secondaryAdvisor={secondaryAdvisor}
          />
        );
      default:
        return <p>Unknown step</p>;
    }
  }

  // Apply the Rebellionaire theme only when both the client type and name match.
  const isRebelClient = clientType === 'rebellionaire';
  const themeClass = isRebelClient ? 'theme-rebellionaire' : '';

  if (loading) {
    return (
      <div className={`d-flex justify-content-center align-items-center vh-100 ${themeClass}`}>
        <div className="text-center">
          <div className="spinner-border text-danger mb-3" role="status" />
          <p className="text-muted">Loading your progress…</p>
        </div>
      </div>
    );
  }

  return (
    <div className={themeClass}>
      {error && (
        <div
          className="alert alert-warning position-fixed bottom-0 start-50 translate-middle-x mb-4 shadow-sm"
          style={{ zIndex: 9999, maxWidth: 500 }}
        >
          <i className="bi bi-exclamation-triangle me-2" />
          {error}
        </div>
      )}

      <WizardLayout
        currentStep={currentStep}
        steps={STEPS}
        onBack={goBack}
        onNext={goNext}
        onSkip={goSkip}
        showSkip={(() => {
          const key = STEPS[currentStep]?.key;
          if (
            !key
            || key === 'welcome'
            || key === 'review'
            || key === 'schedule_meeting'
          ) return false;
          const statuses = computeStepStatuses(formData, STEPS, currentStep);
          return statuses[currentStep] === 'empty';
        })()}
        isFirst={currentStep === 0}
        isLast={currentStep === STEPS.length - 1}
        nextDisabled={(
          STEPS[currentStep]?.key === 'schedule_meeting'
          && !hasScheduledMeeting(formData?.schedule_meeting)
        )}
        saving={saving}
        submitLabel={STEPS[currentStep]?.key === 'review' ? 'Finish' : 'Submit'}
        onStepClick={jumpTo}
        stepStatuses={computeStepStatuses(formData, STEPS, currentStep)}
        lockFutureSteps={!hasScheduledMeeting(formData?.schedule_meeting)}
        clientName={client?.name || ''}
        onLogout={logout}
      >
        {renderStep()}
      </WizardLayout>
    </div>
  );
}
